import React, { useState, useEffect } from 'react';
import { Link, useLoaderData, useNavigate } from 'react-router-dom';
import TopAppBar from '../components/TopAppBar';
import BottomNavBar from '../components/BottomNavBar';
import { useAuth } from '../context/AuthContext';
import { getJobById, cancelJob, markJobIncomplete, confirmJobCompletion } from '../api/job';
import { uploadJobImage } from '../api/message';
import { fetchContractorProfile } from '../api/contractorProfile';
import { getReviewForJob, updateReview, submitReview, JobReviewDto } from '../api/review';
import { requestReview } from '../api/message';
import { Job, JobImage, JobImageType } from '../types/job';
import ShareButtons from '../components/ShareButtons';

export async function jobDetailLoader({ params }: { params: Record<string, string | undefined> }) {
  const job = await getJobById(parseInt(params.jobId!));
  return { job };
}

const statusConfig: Record<string, { label: string; colour: string; icon: string }> = {
  Open:       { label: 'Open',        colour: 'bg-green-100 text-green-800',                        icon: 'radio_button_unchecked' },
  InProgress: { label: 'In Progress', colour: 'bg-blue-100 text-blue-800',                          icon: 'autorenew' },
  Completed:  { label: 'Completed',   colour: 'bg-surface-container text-on-surface-variant',        icon: 'check_circle' },
  Incomplete: { label: 'Incomplete',  colour: 'bg-red-100 text-red-800',                             icon: 'cancel' },
  Cancelled:  { label: 'Cancelled',   colour: 'bg-surface-container text-on-surface-variant',        icon: 'block' },
  Expired:    { label: 'Expired',     colour: 'bg-surface-container text-on-surface-variant',        icon: 'schedule' },
};

function formatBudget(min: number, max: number): string | null {
  if (min <= 0 && max <= 0) return null;
  if (min > 0 && max > 0) return `R${min.toLocaleString()} – R${max.toLocaleString()}`;
  if (min > 0) return `From R${min.toLocaleString()}`;
  return `Up to R${max.toLocaleString()}`;
}

function timeAgo(timestamp: string): string {
  const diff = Date.now() - new Date(timestamp).getTime();
  const days = Math.floor(diff / 86400000);
  if (days === 0) return 'Today';
  if (days === 1) return 'Yesterday';
  if (days < 7) return `${days}d ago`;
  if (days < 30) return `${Math.floor(days / 7)}w ago`;
  if (days < 365) return `${Math.floor(days / 30)}mo ago`;
  return new Date(timestamp).toLocaleDateString('en-ZA', { month: 'short', year: 'numeric' });
}

interface StarRatingProps {
  label: string;
  value: number;
  onChange: (v: number) => void;
}
const StarRating: React.FC<StarRatingProps> = ({ label, value, onChange }) => (
  <div className="flex items-center justify-between">
    <span className="text-sm text-on-surface-variant w-36">{label}</span>
    <div className="flex gap-1">
      {[1, 2, 3, 4, 5].map((star) => (
        <button
          key={star}
          type="button"
          onClick={() => onChange(star)}
          className="focus:outline-none"
          aria-label={`${star} star${star > 1 ? 's' : ''}`}
        >
          <span
            className={`material-symbols-outlined text-xl ${star <= value ? 'text-amber-400' : 'text-outline-variant'}`}
            style={star <= value ? { fontVariationSettings: "'FILL' 1" } : undefined}
          >
            star
          </span>
        </button>
      ))}
    </div>
  </div>
);

const JobDetailPage: React.FC = () => {
  const { job: initialJob } = useLoaderData() as { job: Job };
  const [job, setJob] = useState<Job>(initialJob);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { currentUser } = useAuth();
  const navigate = useNavigate();

  const isOwner = currentUser?.sub === job.clientUserId;
  const status = statusConfig[job.status] ?? { label: job.status, colour: 'bg-surface-container text-on-surface-variant', icon: 'help' };
  const [contractorProfileId, setContractorProfileId] = useState<number | null>(null);
  const [uploadingType, setUploadingType] = useState<JobImageType | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [reviewRequested, setReviewRequested] = useState(job.hasReviewBeenRequested ?? false);
  const [requestingReview, setRequestingReview] = useState(false);
  const [jobReview, setJobReview] = useState<JobReviewDto | null>(null);
  const [reviewSaving, setReviewSaving] = useState(false);
  const [reviewError, setReviewError] = useState<string | null>(null);
  const [reviewQuality, setReviewQuality] = useState(0);
  const [reviewReliability, setReviewReliability] = useState(0);
  const [reviewCommunication, setReviewCommunication] = useState(0);
  const [reviewPricing, setReviewPricing] = useState(0);
  const [reviewComment, setReviewComment] = useState('');

  useEffect(() => {
    if (!currentUser?.isContractor) return;
    fetchContractorProfile().then((p) => {
      if (p?.id) setContractorProfileId(p.id);
    });
  }, [currentUser]);

  useEffect(() => {
    if (!isOwner || (job.status !== 'Completed' && job.status !== 'Incomplete')) return;
    getReviewForJob(job.id).then((r) => {
      if (r) {
        setJobReview(r);
        setReviewQuality(r.qualityRating);
        setReviewReliability(r.reliabilityRating);
        setReviewCommunication(r.communicationRating);
        setReviewPricing(r.pricingRating);
        setReviewComment(r.comment ?? '');
      }
    }).catch(() => {});
  }, [isOwner, job.id, job.status]);

  const handle = (action: () => Promise<Job>) => async () => {
    setLoading(true);
    setError(null);
    try {
      setJob(await action());
    } catch (e: any) {
      setError(e.message ?? 'Something went wrong.');
    } finally {
      setLoading(false);
    }
  };

  const handleUploadImage = async (type: JobImageType, file: File) => {
    setUploadingType(type);
    setUploadError(null);
    try {
      await uploadJobImage(job.id, type, file);
      const updated = await getJobById(job.id);
      setJob(updated);
    } catch (e: any) {
      setUploadError(e.message ?? 'Upload failed.');
    } finally {
      setUploadingType(null);
    }
  };

  const isSelectedContractor =
    currentUser?.isContractor && contractorProfileId !== null && contractorProfileId === job.selectedContractorId;

  const budget = formatBudget(job.budgetMin, job.budgetMax);
  const isClosedStatus = job.status === 'Completed' || job.status === 'Incomplete' || job.status === 'Cancelled' || job.status === 'Expired';

  return (
    <div className="bg-background text-on-background min-h-screen pb-24 md:pb-0">
      <TopAppBar />

      <main className="max-w-3xl mx-auto px-4 pt-6 pb-12">
        {/* Back */}
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-1.5 text-secondary font-label-bold mb-5 hover:underline text-sm"
        >
          <span className="material-symbols-outlined text-[18px]">arrow_back</span>
          Back
        </button>

        {/* Hero image */}
        {job.pictureUrl && (
          <div className="rounded-2xl overflow-hidden mb-4 aspect-video bg-surface-container-low">
            <img src={job.pictureUrl} alt={job.title} className="w-full h-full object-cover" />
          </div>
        )}

        {/* Header card */}
        <div className="bg-surface rounded-2xl border border-outline-variant/30 shadow-sm p-6 mb-4">
          <div className="flex items-start justify-between gap-4 mb-3">
            <h1 className="font-headline-md text-on-surface leading-tight">{job.title}</h1>
            <div className="flex items-center gap-2 flex-shrink-0">
              <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-label-bold ${status.colour}`}>
                <span className="material-symbols-outlined text-[13px]" style={{ fontVariationSettings: "'FILL' 1" }}>{status.icon}</span>
                {status.label}
              </span>
              <ShareButtons url={`${window.location.origin}/jobs/${job.id}`} title={job.title} />
            </div>
          </div>

          {/* Metadata chips */}
          <div className="flex flex-wrap gap-x-4 gap-y-2 text-sm text-on-surface-variant mb-4">
            <span className="flex items-center gap-1.5">
              <span className="material-symbols-outlined text-[16px] text-primary">location_on</span>
              {job.location}
            </span>
            {job.categoryName && (
              <span className="flex items-center gap-1.5">
                <span className="material-symbols-outlined text-[16px] text-primary">category</span>
                {job.categoryName}
              </span>
            )}
            {budget && (
              <span className="flex items-center gap-1.5 font-label-bold text-primary">
                <span className="material-symbols-outlined text-[16px]">payments</span>
                {budget}
              </span>
            )}
            <span className="flex items-center gap-1.5">
              <span className="material-symbols-outlined text-[16px]">schedule</span>
              Posted {timeAgo(job.createdAt)}
            </span>
            {job.clientDisplayName && (
              <span className="flex items-center gap-1.5">
                <span className="material-symbols-outlined text-[16px]">person</span>
                {job.clientDisplayName}
              </span>
            )}
          </div>

          <p className="text-on-surface text-body-md whitespace-pre-wrap leading-relaxed">{job.description}</p>
        </div>

        {/* Selected contractor card — visible to client/public when job is InProgress or closed */}
        {job.selectedContractorId && !currentUser?.isContractor && (job.status === 'InProgress' || isClosedStatus) && (
          <div className="bg-surface rounded-2xl border border-outline-variant/30 shadow-sm p-5 mb-4 flex items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-primary-container flex items-center justify-center flex-shrink-0">
                <span className="material-symbols-outlined text-primary text-[20px]" style={{ fontVariationSettings: "'FILL' 1" }}>person</span>
              </div>
              <div>
                <p className="text-xs text-on-surface-variant">Selected contractor</p>
                <p className="font-label-bold text-on-surface text-sm">Contractor #{job.selectedContractorId}</p>
              </div>
            </div>
            <Link
              to={`/contractors/${job.selectedContractorId}`}
              className="flex items-center gap-1.5 text-sm font-label-bold text-secondary hover:underline"
            >
              View profile
              <span className="material-symbols-outlined text-[16px]">arrow_forward</span>
            </Link>
          </div>
        )}

        {/* Job Images */}
        {((job.jobImages && job.jobImages.length > 0) || isSelectedContractor || (isOwner && (job.status === 'Completed' || job.status === 'Incomplete'))) && (
          <div className="bg-surface rounded-2xl border border-outline-variant/30 shadow-sm p-6 mb-4">
            <h2 className="font-title-lg text-on-surface mb-4 flex items-center gap-2">
              <span className="material-symbols-outlined text-primary text-[20px]" style={{ fontVariationSettings: "'FILL' 1" }}>photo_library</span>
              Job Photos
            </h2>

            {(['BeforeWork', 'AfterWork', 'ClientCompletion'] as JobImageType[]).map((type) => {
              const images = job.jobImages?.filter((img) => img.type === type) ?? [];
              const labels: Record<JobImageType, string> = {
                BeforeWork: 'Before Work',
                AfterWork: 'After Work',
                ClientCompletion: 'Completion',
              };
              if (images.length === 0) return null;
              return (
                <div key={type} className="mb-4">
                  <p className="text-sm font-label-bold text-on-surface-variant mb-2">{labels[type]}</p>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                    {images.map((img) => (
                      <a key={img.id} href={img.url} target="_blank" rel="noopener noreferrer">
                        <img
                          src={img.url}
                          alt={labels[type]}
                          className="w-full aspect-square object-cover rounded-xl border border-outline-variant/20 hover:opacity-90 transition-opacity"
                        />
                      </a>
                    ))}
                  </div>
                </div>
              );
            })}

            <div className="flex flex-wrap gap-3 mt-2">
              {isSelectedContractor && job.status === 'InProgress' && (
                <>
                  {(['BeforeWork', 'AfterWork'] as JobImageType[]).map((type) => (
                    <label
                      key={type}
                      className={`cursor-pointer flex items-center gap-2 px-4 py-2 rounded-xl border border-outline-variant text-sm font-label-bold hover:bg-surface-container transition-colors ${uploadingType === type ? 'opacity-50 pointer-events-none' : ''}`}
                    >
                      <span className="material-symbols-outlined text-[16px]">add_photo_alternate</span>
                      {uploadingType === type ? 'Uploading...' : type === 'BeforeWork' ? 'Before Photo' : 'After Photo'}
                      <input
                        type="file"
                        accept="image/jpeg,image/png,image/webp"
                        className="hidden"
                        onChange={(e) => {
                          const f = e.target.files?.[0];
                          if (f) handleUploadImage(type, f);
                          e.target.value = '';
                        }}
                      />
                    </label>
                  ))}
                </>
              )}
              {isOwner && (job.status === 'Completed' || job.status === 'Incomplete') && (
                <label className={`cursor-pointer flex items-center gap-2 px-4 py-2 rounded-xl border border-outline-variant text-sm font-label-bold hover:bg-surface-container transition-colors ${uploadingType === 'ClientCompletion' ? 'opacity-50 pointer-events-none' : ''}`}>
                  <span className="material-symbols-outlined text-[16px]">add_photo_alternate</span>
                  {uploadingType === 'ClientCompletion' ? 'Uploading...' : 'Completion Photo'}
                  <input
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    className="hidden"
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      if (f) handleUploadImage('ClientCompletion', f);
                      e.target.value = '';
                    }}
                  />
                </label>
              )}
            </div>
            {uploadError && <p className="text-error text-sm mt-2">{uploadError}</p>}
          </div>
        )}

        {/* Completion progress */}
        {job.status === 'InProgress' && (
          <div className="bg-blue-50 rounded-2xl border border-blue-100 p-5 mb-4">
            <p className="font-label-bold text-blue-800 mb-3 flex items-center gap-2">
              <span className="material-symbols-outlined text-[18px]">pending_actions</span>
              Awaiting completion confirmation
            </p>
            <div className="flex gap-6 text-sm">
              <span className={`flex items-center gap-1.5 ${job.clientConfirmedCompletion ? 'text-green-700 font-label-bold' : 'text-blue-600'}`}>
                <span className="material-symbols-outlined text-[16px]" style={job.clientConfirmedCompletion ? { fontVariationSettings: "'FILL' 1" } : undefined}>
                  {job.clientConfirmedCompletion ? 'check_circle' : 'radio_button_unchecked'}
                </span>
                Client confirmed
              </span>
              <span className={`flex items-center gap-1.5 ${job.contractorConfirmedCompletion ? 'text-green-700 font-label-bold' : 'text-blue-600'}`}>
                <span className="material-symbols-outlined text-[16px]" style={job.contractorConfirmedCompletion ? { fontVariationSettings: "'FILL' 1" } : undefined}>
                  {job.contractorConfirmedCompletion ? 'check_circle' : 'radio_button_unchecked'}
                </span>
                Contractor confirmed
              </span>
            </div>
          </div>
        )}

        {error && <p className="text-error text-sm mb-4">{error}</p>}

        {/* Actions */}
        <div className="flex flex-wrap gap-3">
          {/* Not logged in: prompt to register or login */}
          {!currentUser && job.status === 'Open' && (
            <>
              <button
                onClick={() => navigate('/register')}
                className="bg-primary text-on-primary px-5 py-2.5 rounded-xl font-label-bold hover:bg-primary/90 transition-colors"
              >
                Register to contact client
              </button>
              <button
                onClick={() => navigate('/login')}
                className="border border-outline-variant text-on-surface px-5 py-2.5 rounded-xl font-label-bold hover:bg-surface-container transition-colors"
              >
                Log in
              </button>
            </>
          )}

          {/* Contractor: contact client (Open, or InProgress if selected) */}
          {!isOwner && currentUser?.isContractor && (
            job.status === 'Open' || (job.status === 'InProgress' && isSelectedContractor)
          ) && (
            <button
              onClick={() => navigate(`/messages?jobId=${job.id}&contractorProfileId=${contractorProfileId}`)}
              disabled={contractorProfileId === null}
              className="bg-primary text-on-primary px-5 py-2.5 rounded-xl font-label-bold hover:bg-primary/90 transition-colors disabled:opacity-50 flex items-center gap-2"
            >
              <span className="material-symbols-outlined text-[18px]">chat_bubble</span>
              Contact Client
            </button>
          )}

          {/* Client/Contractor: confirm completion */}
          {job.status === 'InProgress' && currentUser && (
            <button
              onClick={handle(() => confirmJobCompletion(job.id))}
              disabled={loading}
              className="bg-primary text-on-primary px-5 py-2.5 rounded-xl font-label-bold hover:bg-primary/90 transition-colors disabled:opacity-50 flex items-center gap-2"
            >
              <span className="material-symbols-outlined text-[18px]">check_circle</span>
              Confirm Completion
            </button>
          )}

          {/* Client: mark incomplete */}
          {isOwner && job.status === 'InProgress' && (
            <button
              onClick={handle(() => markJobIncomplete(job.id))}
              disabled={loading}
              className="border border-error text-error px-5 py-2.5 rounded-xl font-label-bold hover:bg-error/5 transition-colors disabled:opacity-50"
            >
              Mark Incomplete
            </button>
          )}

          {/* Client: view all messages — available for all statuses */}
          {isOwner && (
            <button
              onClick={() => navigate(`/messages?jobId=${job.id}`)}
              className="bg-secondary text-on-secondary px-5 py-2.5 rounded-xl font-label-bold hover:bg-secondary/90 transition-colors flex items-center gap-2"
            >
              <span className="material-symbols-outlined text-[18px]">chat_bubble</span>
              Messages{job.threadCount ? ` (${job.threadCount})` : ''}
            </button>
          )}

          {/* Client: cancel open job */}
          {isOwner && job.status === 'Open' && (
            <button
              onClick={handle(() => cancelJob(job.id))}
              disabled={loading}
              className="border border-error text-error px-5 py-2.5 rounded-xl font-label-bold hover:bg-error/5 transition-colors disabled:opacity-50"
            >
              Cancel Job
            </button>
          )}

          {/* Contractor: request review after job closes */}
          {isSelectedContractor && (job.status === 'Completed' || job.status === 'Incomplete') && (
            <button
              onClick={async () => {
                if (reviewRequested) return;
                setRequestingReview(true);
                try {
                  await requestReview(job.id);
                  setReviewRequested(true);
                } catch {
                  setReviewRequested(true);
                } finally {
                  setRequestingReview(false);
                }
              }}
              disabled={reviewRequested || requestingReview}
              className="border border-outline-variant text-on-surface px-5 py-2.5 rounded-xl font-label-bold hover:bg-surface-container transition-colors disabled:opacity-50 disabled:cursor-default"
            >
              {reviewRequested ? 'Review requested' : requestingReview ? 'Requesting...' : 'Request Review'}
            </button>
          )}

          {/* Client: my jobs shortcut */}
          {isOwner && (
            <Link
              to="/my-jobs"
              className="text-secondary font-label-bold px-5 py-2.5 rounded-xl border border-outline-variant hover:bg-surface-container transition-colors"
            >
              My Jobs
            </Link>
          )}
        </div>

        {/* Review form — client on completed jobs */}
        {isOwner && (job.status === 'Completed' || job.status === 'Incomplete') && job.selectedContractorId && (
          <div className="bg-surface rounded-2xl border border-outline-variant/30 shadow-sm p-6 mt-4">
            <h2 className="font-title-lg text-on-surface mb-4 flex items-center gap-2">
              <span className="material-symbols-outlined text-amber-400 text-[20px]" style={{ fontVariationSettings: "'FILL' 1" }}>star</span>
              {jobReview ? 'Your review' : 'Leave a review'}
            </h2>
            <form
              onSubmit={async (e) => {
                e.preventDefault();
                if (!reviewQuality || !reviewReliability || !reviewCommunication || !reviewPricing) return;
                setReviewSaving(true);
                setReviewError(null);
                try {
                  const ratings = {
                    qualityRating: reviewQuality,
                    reliabilityRating: reviewReliability,
                    communicationRating: reviewCommunication,
                    pricingRating: reviewPricing,
                    comment: reviewComment,
                  };
                  if (jobReview) {
                    await updateReview(jobReview.id, ratings);
                  } else {
                    await submitReview(
                      { ...ratings, contractorProfileId: job.selectedContractorId!, jobId: job.id },
                    );
                  }
                  const updated = await getReviewForJob(job.id);
                  setJobReview(updated);
                } catch (err: any) {
                  setReviewError(err?.response?.data || 'Failed to save review.');
                } finally {
                  setReviewSaving(false);
                }
              }}
              className="space-y-4"
            >
              <StarRating label="Quality"       value={reviewQuality}       onChange={setReviewQuality} />
              <StarRating label="Reliability"   value={reviewReliability}   onChange={setReviewReliability} />
              <StarRating label="Communication" value={reviewCommunication} onChange={setReviewCommunication} />
              <StarRating label="Pricing"       value={reviewPricing}       onChange={setReviewPricing} />
              <textarea
                className="w-full border border-outline-variant rounded-xl p-3 text-sm bg-surface-container-lowest focus:outline-none focus:ring-1 focus:ring-primary resize-none"
                rows={3}
                placeholder="Share your experience (optional)"
                value={reviewComment}
                onChange={(e) => setReviewComment(e.target.value)}
                maxLength={1000}
              />
              {reviewError && <p className="text-sm text-error">{reviewError}</p>}
              <button
                type="submit"
                disabled={!reviewQuality || !reviewReliability || !reviewCommunication || !reviewPricing || reviewSaving}
                className="bg-primary text-on-primary font-label-bold py-2.5 px-6 rounded-xl disabled:opacity-50 hover:bg-primary/90 transition-colors"
              >
                {reviewSaving ? 'Saving...' : jobReview ? 'Update review' : 'Submit review'}
              </button>
            </form>
          </div>
        )}
      </main>

      <BottomNavBar />
    </div>
  );
};

export default JobDetailPage;
